'use client'

import { useAppDispatch } from '@/lib/store'
import { ProductAttributeKey } from '@/lib/types/core/product-attribute-keys'
import { getCartItemLineTotal, removeFromCart, resolveCartItemUnitPrice } from '@/lib/store/features/cart/cartSlice'
import { CartLineItem } from '@/lib/types/operations/cart'
import type { PriceTier } from '@/lib/utils/price-resolution'
import { formatUnit } from '@/lib/utils/unit-conversion'
import { ImagePlaceholder } from '@/components/ui/image-placeholder'

interface CartItemCollapsedProps {
  item: CartLineItem
  onExpand: () => void
  priceTier: PriceTier
}

/**
 * The cart-line spec string: the full selection identity, so a length is never
 * separable from what it belongs to (spec Screen C: "2 pieces x 5 yd", never a
 * bare "Qty 1"). Exported for unit tests.
 */
export function getSpecLine(item: Pick<CartLineItem, 'purchaseMode' | 'pieces' | 'lengthPerPiece' | 'quantity' | 'unitOfMeasure' | 'variant' | 'product'>): string {
  const unit = formatUnit(item.unitOfMeasure ?? 'FOOT')

  switch (item.purchaseMode) {
    case 'custom-cut': {
      const pieces = item.pieces ?? 1
      const length = item.lengthPerPiece ?? 0
      if (pieces > 1) {
        return `${pieces} pieces x ${length} ${unit} = ${length * pieces} ${unit} total`
      }
      return `Cut to length, ${length} ${unit}`
    }
    case 'full-roll': {
      const rollLength = item.variant?.rollLengthUnits
        ?? item.variant?.attributeValues?.[ProductAttributeKey.ROLL_LENGTH]
        ?? item.product.rollLengthUnits
        ?? '?'
      const rolls = item.quantity ?? 1
      return rolls > 1 ? `${rolls} x full roll (${rollLength} ${unit})` : `Full roll (${rollLength} ${unit})`
    }
    case 'fixed':
    default:
      return `${item.quantity} x Kit/Item`
  }
}

/** The per-unit rate line shown under the spec ("$73.84/yd x 10 yd"). */
export function getRateLine(item: CartLineItem, priceTier: PriceTier): string | null {
  const unitPrice = resolveCartItemUnitPrice(item, priceTier)
  if (!unitPrice) return null
  switch (item.purchaseMode) {
    case 'custom-cut': {
      const unit = formatUnit(item.unitOfMeasure ?? 'FOOT')
      const totalUnits = (item.lengthPerPiece ?? 0) * (item.pieces ?? 1)
      return `$${unitPrice.toFixed(2)}/${unit} x ${totalUnits} ${unit}`
    }
    case 'full-roll':
      return `$${unitPrice.toFixed(2)}/roll`
    default:
      return null
  }
}

function getThumbnailUrl(item: CartLineItem): string | null {
  // Try variant media first
  if (item.variant?.media?.url) return item.variant.media.url
  // Then product media
  if (item.product.media?.length) return item.product.media[0].url
  return null
}

export function CartItemCollapsed({ item, onExpand, priceTier }: CartItemCollapsedProps) {
  const dispatch = useAppDispatch()
  const lineTotal = getCartItemLineTotal(item, priceTier)
  const specLine = getSpecLine(item)
  const rateLine = getRateLine(item, priceTier)
  const thumbnail = getThumbnailUrl(item)
  const swatchHex = (item.variant as { swatchHex?: string | null } | undefined)?.swatchHex ?? null

  return (
    <div className="flex items-center gap-3 rounded-lg bg-surface p-3">
      {/* Thumbnail: photo when one exists, else an honest image placeholder
          whose alt text states the needed asset (with the variant's colour
          dot for scannability) */}
      <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-overlay">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={item.product.name ?? 'Product'}
            className="h-full w-full object-cover"
          />
        ) : (
          <ImagePlaceholder
            alt={`Image needed: product photo of ${item.product.name ?? 'this product'}`}
            className="h-full w-full"
            rounded="rounded-md"
            iconSize="sm"
          />
        )}
        {!thumbnail && swatchHex && (
          <span
            className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border border-divider"
            style={{ backgroundColor: swatchHex }}
            aria-hidden="true"
          />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {item.product.name}
        </p>
        <p className="text-xs text-muted">{specLine}</p>
        {rateLine && <p className="text-xs text-muted tabular-nums">{rateLine}</p>}
        <button
          type="button"
          onClick={onExpand}
          className="mt-0.5 text-xs text-foreground hover:underline"
        >
          Edit details
        </button>
      </div>

      {/* Price + remove */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="text-sm font-semibold text-foreground tabular-nums">
          ${lineTotal.toFixed(2)}
        </span>
        <button
          type="button"
          onClick={() => dispatch(removeFromCart({ cartItemId: item.id }))}
          className="text-muted hover:text-danger transition-colors text-lg leading-none"
          aria-label={`Remove ${item.product.name} from cart`}
        >
          &times;
        </button>
      </div>
    </div>
  )
}
